import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { dataSourceAdapterBaseConfigSchema } from "./shared-config";
import { csvCharField } from "../csv-char-field";

export const csvDataSourceAdapterStep = defineStepConfig({
  type: "csv_datasource_adapter",
  name: "Csv DataSource Adapter",
  description: "Transform a CSV file in Azure Blob Storage.",
  category: "DataSource Adapter",

  inputSchema: z.object({
    blobStorageUri: z.string().describe("Blob Storage URI of the source CSV"),
    storageProfile: z
      .string()
      .describe(
        "Storage profile key that identifies the credentials to use to access the Blob Storage URI",
      ),
  }),

  outputSchema: z.object({
    blobStorageUris: z
      .array(z.string())
      .describe(
        "Blob Storage URIs of the transformed CSV chunks. Each URI can be fanned out to a downstream step that accepts a single URI as input.",
      ),
    storageProfile: z
      .string()
      .describe(
        "Storage profile key that identifies the credentials to use to access the Blob Storage URIs",
      ),
    dataSourceLinks: z
      .array(z.string().uuid())
      .describe("DataIngestLink UUIDs resolved from the configured strategy"),
    csvOptions: z
      .object({
        delimiter: z.string().optional(),
        quote: z.string().optional(),
        encoding: z.string().optional(),
        headerRowIndex: z.number().int().min(-1).optional(),
      })
      .describe(
        "CSV parsing options this step was configured with, propagated for use by downstream steps.",
      ),
  }),

  configSchema: dataSourceAdapterBaseConfigSchema.extend({
    mappingStrategy: z
      .enum(["strongly_typed", "eager_evaluation", "pass_through"])
      .optional()
      .describe("Strategy for mapping CSV headers to destination columns"),

    // strongly_typed: ordered CSV header → column + type pairs
    stronglyTypedMappings: z
      .array(
        z.object({
          csvHeader: z.string(),
          column: z.string(),
          type: z
            .enum(["string", "number", "integer", "float", "boolean", "date"])
            .optional(),
        }),
      )
      .optional(),

    // eager_evaluation: flexible header → column mappings + filter list
    eagerMappings: z
      .array(z.object({ csvHeader: z.string(), column: z.string() }))
      .optional(),
    headersToRemove: z.array(z.string()).optional(),
    unknownColumnHandling: z.enum(["remove", "write_as_is"]).optional(),

    delimiter: csvCharField
      .optional()
      .describe("CSV field delimiter (default: comma)"),
    quoteChar: csvCharField
      .optional()
      .describe("CSV quote character. Leave unset to disable quoting."),
    encoding: z
      .string()
      .optional()
      .describe("CSV file encoding (default: utf-8)"),
    trimWhitespace: z
      .boolean()
      .optional()
      .describe("Trim leading and trailing whitespace from field values"),
    headerRowIndex: z
      .number()
      .int()
      .min(-1)
      .optional()
      .describe(
        "0-based index of the header row. -1 means the file has no header row.",
      ),

    columnMappingStrategy: z
      .enum(["column_index", "header_mapping"])
      .optional()
      .describe(
        "How CSV columns are matched to destination schema fields. " +
          "'column_index' maps by position (order in the CSV); " +
          "'header_mapping' maps by matching a CSV header name to each schema field.",
      ),
    columnIndexMappings: z
      .array(z.object({ column: z.string(), index: z.number().int().min(0) }))
      .optional()
      .describe(
        "Explicit column index mappings for column_index strategy. " +
          "Each entry maps a schema fieldName to its 0-based CSV column index. " +
          "Entries must have unique, non-negative integer indices.",
      ),
    headerMappings: z
      .array(z.object({ column: z.string(), csvHeader: z.string() }))
      .optional()
      .describe(
        "Maps schema fieldNames to CSV header names for header_mapping strategy.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
